import { AlertBar2 } from "@/ui2/atoms/AlertBar";
import type { AlertProps } from "@/ui2/atoms/AlertBar";
import { formatTimestamp } from "@/component-library/utils";
import type { PatientDetails } from "@/core/types";
import { testLocator } from "@/lib/helpers/test-locator";

import { formatSoftPaywallExpirationTimestamp } from "../helpers/formatExpirationTimestamp";

type PaywallStatus = {
  last_failed_transaction_date?: string;
  paywall: boolean;
  type?: "soft_paywall" | "hard_paywall_add_payment" | "hard_paywall_update_payment";
};

type Props = Omit<AlertProps, "variant" | "children"> & {
  automaticPaymentEnabled: boolean;
  patientName: string;
  paywallStatus: PaywallStatus | undefined;
  softPaywallTimestamp: PatientDetails["soft_paywall"];
};

export function PaymentAlertBanner({
  automaticPaymentEnabled,
  patientName,
  paywallStatus,
  softPaywallTimestamp,
  ...props
}: Props) {
  if (!paywallStatus?.paywall || !automaticPaymentEnabled) return null;

  const renderContent = () => {
    switch (paywallStatus.type) {
      case "soft_paywall": {
        const suffix = softPaywallTimestamp ? (
          <>
            has until{" "}
            <span data-private>{formatSoftPaywallExpirationTimestamp(softPaywallTimestamp)}</span>{" "}
            to update their payment method.
          </>
        ) : (
          "needs to update their payment method soon to avoid any disruption to care."
        );

        return (
          <span data-public>
            <strong className="tw:mr-ch">Patient action needed:</strong>
            <span data-private>{patientName}</span> {suffix} They can still access the platform and
            attend sessions until then.
          </span>
        );
      }

      case "hard_paywall_add_payment":
        return (
          <span data-public {...testLocator("hard-paywall-banner-add-payment")}>
            <strong className="tw:mr-ch">Patient access blocked:</strong>
            <span data-private>{patientName}</span> did not update their payment method within 24
            hours and is now locked out of the platform. Do not schedule or hold sessions until
            access is restored.
          </span>
        );

      case "hard_paywall_update_payment":
        return (
          <span data-public {...testLocator("hard-paywall-banner-update-payment")}>
            <strong className="tw:mr-ch">Patient access blocked due to failed payment:</strong> A
            charge attempt on{" "}
            <span data-private>{formatTimestamp(paywallStatus.last_failed_transaction_date)}</span>{" "}
            was unsuccessful. <span data-private>{patientName}</span> is locked out of the platform
            and must update their payment method to resume care.
          </span>
        );

      default:
        return null;
    }
  };

  return (
    <AlertBar2 variant="error" {...props}>
      {renderContent()}
    </AlertBar2>
  );
}
